// Ontology section for System Architecture - 165 lines ✅
import { useState } from 'react'
import { Network, ChevronDown, ChevronRight } from 'lucide-react'

interface TreeNode {
  name: string
  description?: string
  children?: TreeNode[]
}

const ontology: TreeNode = {
  name: 'Platform',
  description: 'D365 AI Assistant Platform Root',
  children: [
    {
      name: 'Organization (Tenant)',
      description: 'Multi-tenant isolation boundary',
      children: [
        {
          name: 'Users',
          description: 'System users with role-based access',
          children: [
            { name: 'Admins', description: 'System and organization administrators' },
            { name: 'Experts', description: 'Subject matter experts' },
            { name: 'Standard Users', description: 'Regular users' }
          ]
        },
        {
          name: 'Customers',
          description: 'End customer organizations',
          children: [
            { name: 'Customer Profiles', description: 'Customer metadata' },
            { name: 'Customer Documents', description: 'Uploaded files' }
          ]
        },
        {
          name: 'Issues',
          description: 'Support tickets and problems',
          children: [
            { name: 'Conversation Sessions', description: 'Chat-based issue recording' },
            { name: 'Expert Assignments', description: 'Routing to experts' }
          ]
        },
        {
          name: 'Knowledge Base',
          description: 'Solutions repository',
          children: [
            { name: 'Knowledge Entries', description: 'Validated solutions' },
            { name: 'Resolution Methods', description: 'Step-by-step procedures' }
          ]
        },
        {
          name: 'Knowledge Graph',
          description: 'Entity relationship graph',
          children: [
            { name: 'Nodes', description: 'Graph nodes representing entities' },
            { name: 'Edges', description: 'Relationships between nodes' }
          ]
        }
      ]
    }
  ]
}

function TreeNodeComponent({ node, level = 0 }: { node: TreeNode; level?: number }) {
  const [isExpanded, setIsExpanded] = useState(level < 2)
  const hasChildren = node.children && node.children.length > 0

  const indentClass = `ml-${level * 4}`
  const levelColors = [
    'text-kaycoo-teal-dark font-bold text-lg',
    'text-kaycoo-teal-dark font-semibold',
    'text-kaycoo-charcoal font-medium',
    'text-kaycoo-charcoal-2'
  ]

  return (
    <div className={indentClass}>
      <div
        className={`flex cursor-pointer items-start gap-2 py-1 ${hasChildren ? 'hover:bg-kaycoo-teal-pale' : ''}`}
        onClick={() => hasChildren && setIsExpanded(!isExpanded)}
      >
        {hasChildren && (
          <span className="mt-1">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-kaycoo-teal-dark" />
            ) : (
              <ChevronRight className="h-4 w-4 text-kaycoo-teal-dark" />
            )}
          </span>
        )}
        {!hasChildren && <span className="ml-6" />}
        <div className="flex-1">
          <div className={levelColors[Math.min(level, levelColors.length - 1)]}>{node.name}</div>
          {node.description && (
            <div className="text-xs text-kaycoo-charcoal-2">{node.description}</div>
          )}
        </div>
      </div>
      {hasChildren && isExpanded && (
        <div className="ml-4 border-l-2 border-kaycoo-teal-pale pl-2">
          {node.children!.map((child, i) => (
            <TreeNodeComponent key={i} node={child} level={level + 1} />
          ))}
        </div>
      )}
    </div>
  )
}

function DataModelConstraints() {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="rounded-lg border border-kaycoo-teal-dark bg-white p-4">
        <h4 className="mb-3 font-semibold text-kaycoo-teal-dark">Multi-Tenancy Rules</h4>
        <ul className="space-y-2 text-sm text-kaycoo-charcoal-2">
          <li>• Every entity MUST have organization_id</li>
          <li>• RLS policies enforce organization isolation</li>
          <li>• No cross-tenant data access allowed</li>
          <li>• Foreign keys maintain referential integrity</li>
        </ul>
      </div>

      <div className="rounded-lg border border-kaycoo-teal-dark bg-white p-4">
        <h4 className="mb-3 font-semibold text-kaycoo-teal-dark">Validation Rules</h4>
        <ul className="space-y-2 text-sm text-kaycoo-charcoal-2">
          <li>• KB entries require expert validation before search</li>
          <li>• Success rate = success_count / (success + failure)</li>
          <li>• Embeddings required for semantic search</li>
          <li>• D365 version must match supported list</li>
        </ul>
      </div>

      <div className="rounded-lg border border-kaycoo-teal-dark bg-white p-4">
        <h4 className="mb-3 font-semibold text-kaycoo-teal-dark">Privacy & Security</h4>
        <ul className="space-y-2 text-sm text-kaycoo-charcoal-2">
          <li>• GDPR Article 25 (Privacy by Design)</li>
          <li>• EU data residency (Frankfurt region)</li>
          <li>• Row-Level Security on all tables</li>
          <li>• Complete audit trail for all actions</li>
        </ul>
      </div>

      <div className="rounded-lg border border-kaycoo-teal-dark bg-white p-4">
        <h4 className="mb-3 font-semibold text-kaycoo-teal-dark">Data Quality</h4>
        <ul className="space-y-2 text-sm text-kaycoo-charcoal-2">
          <li>• Human-in-the-loop validation required</li>
          <li>• Duplicate detection (similarity {'>'} 0.85)</li>
          <li>• Auto-categorization with AI assistance</li>
          <li>• Quality scoring for KB entries</li>
        </ul>
      </div>
    </div>
  )
}

export function OntologySection() {
  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-kaycoo-teal-dark bg-white p-6">
        <div className="mb-4 flex items-center gap-2">
          <Network className="h-6 w-6 text-kaycoo-teal-dark" />
          <h2 className="text-2xl font-bold text-kaycoo-teal-dark">Domain Model Ontology</h2>
        </div>
        <p className="text-kaycoo-charcoal-2">
          Hierarchical structure of the application domain model and conceptual relationships.
        </p>
      </div>

      <div className="rounded-lg border border-kaycoo-teal-dark bg-white p-6">
        <h3 className="mb-4 text-lg font-semibold text-kaycoo-teal-dark">Hierarchy Tree</h3>
        <TreeNodeComponent node={ontology} />
      </div>

      <div className="rounded-lg border border-kaycoo-teal-dark bg-white p-6">
        <h3 className="mb-4 text-lg font-semibold text-kaycoo-teal-dark">Data Model Constraints & Rules</h3>
        <DataModelConstraints />
      </div>
    </div>
  )
}
